import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import api, { API_BASE } from '../api';
import { useAuth } from '../context/auth';
import { Card, Form, Input, Button, FormError } from '../components/FormStyle';
import { PageTitle } from '../components/Style';
import { usePlayback } from '../context/playback';
import StreamingServicesPicker from '../components/StreamingServicesPicker';
import resizeImage from '../utils/resizeImage';

var Textarea = styled.textarea`
  padding: 1rem;
  border: 1px solid #999;
  margin-bottom: 1rem;
  font-size: 0.8rem;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
  background-color: #fff;
  color: #000;
`;

var ProfilePicPreview = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 1rem;
  border: 3px solid #eee;
  cursor: pointer;
`;

var ProfilePicPlaceholder = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: #f5f5f5;
  border: 2px dashed #ccc;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  color: #999;
  font-size: 0.7rem;
  cursor: pointer;
  gap: 0.25rem;

  &:hover {
    border-color: #999;
    background: #eee;
  }
`;

var SuccessMessage = styled.div`
  background-color: #4caf50;
  margin: 0.5rem 0.5rem;
  padding: 0.5rem 0.5rem;
  border-radius: 8px;
  color: white;
`;

var DangerZone = styled.div`
  width: 100%;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #eee;
`;

var DeleteButton = styled.button`
  background: none;
  border: none;
  color: #999;
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: #c00;
  }
`;

var ConfirmDeleteButton = styled.button`
  background: #c00;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #a00;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

var CancelButton = styled.button`
  background: none;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  cursor: pointer;

  &:hover {
    border-color: #999;
  }
`;

var Label = styled.label`
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 0.25rem;
`;

function EditProfile() {
  useEffect(function () {
    document.title = 'Edit Profile | HeavyRotation';
  }, []);

  var authContext = useAuth();
  var authTokens = authContext.authTokens;
  var setAuthTokens = authContext.setAuthTokens;

  var displayNameState = useState('');
  var displayName = displayNameState[0];
  var setDisplayName = displayNameState[1];

  var usernameState = useState('');
  var username = usernameState[0];
  var setUsername = usernameState[1];

  var locationState = useState('');
  var location = locationState[0];
  var setLocation = locationState[1];

  var bioState = useState('');
  var bio = bioState[0];
  var setBio = bioState[1];

  var linksState = useState('');
  var links = linksState[0];
  var setLinks = linksState[1];

  var pictureURLState = useState('');
  var pictureURL = pictureURLState[0];
  var setPictureURL = pictureURLState[1];

  var fileState = useState(null);
  var selectedFile = fileState[0];
  var setSelectedFile = fileState[1];

  var previewState = useState('');
  var previewURL = previewState[0];
  var setPreviewURL = previewState[1];

  var errorState = useState('');
  var error = errorState[0];
  var setError = errorState[1];

  var successState = useState('');
  var success = successState[0];
  var setSuccess = successState[1];

  var digestOptOutState = useState(false);
  var digestOptOut = digestOptOutState[0];
  var setDigestOptOut = digestOptOutState[1];

  var streamingState = useState([]);
  var selectedServices = streamingState[0];
  var setSelectedServices = streamingState[1];

  var otherState = useState('');
  var otherService = otherState[0];
  var setOtherService = otherState[1];

  var loadingState = useState(false);
  var isLoading = loadingState[0];
  var setIsLoading = loadingState[1];

  var showDeleteState = useState(false);
  var showDelete = showDeleteState[0];
  var setShowDelete = showDeleteState[1];

  var confirmTextState = useState('');
  var confirmText = confirmTextState[0];
  var setConfirmText = confirmTextState[1];

  var deletingState = useState(false);
  var isDeleting = deletingState[0];
  var setIsDeleting = deletingState[1];

  var fileInputRef = useRef(null);
  var navigate = useNavigate();
  var playback = usePlayback();

  useEffect(function () {
    api
      .get('/api/users/editprofile')
      .then(function (result) {
        setDisplayName(result.data.displayName || '');
        setLocation(result.data.location || '');
        setUsername(result.data.username || '');
        setBio(result.data.bio || '');
        setLinks(result.data.links || '');
        setPictureURL(result.data.profilePictureURL || '');
        setDigestOptOut(result.data.digestOptOut || false);
        if (result.data.streamingServices) {
          var parts = result.data.streamingServices.split(',');
          var known = [];
          var other = '';
          parts.forEach(function (p) {
            if (p.startsWith('other:')) {
              other = p.slice(6);
            } else {
              known.push(p);
            }
          });
          setSelectedServices(known);
          setOtherService(other);
        }
      })
      .catch(function () {
        setError('Failed to load profile.');
      });
  }, []);

  function handleFileChange(e) {
    var file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('Image must be under 10 MB. Please choose a smaller file.');
        e.target.value = '';
        return;
      }
      setError('');
      resizeImage(file).then(function (resized) {
        setSelectedFile(resized);
        var reader = new FileReader();
        reader.onloadend = function () {
          setPreviewURL(reader.result);
        };
        reader.readAsDataURL(resized);
      });
    }
  }

  function handleSave() {
    setIsLoading(true);
    setError('');
    setSuccess('');

    var uploadPromise;
    if (selectedFile) {
      var formData = new FormData();
      formData.append('data', selectedFile);
      formData.append('filename', selectedFile.name);
      uploadPromise = api
        .post('/api/users/profile-picture', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(function (result) {
          setPictureURL(result.data.profilePictureURL || '');
        });
    } else {
      uploadPromise = Promise.resolve();
    }

    var services = selectedServices.slice();
    if (otherService.trim()) {
      services.push('other:' + otherService.trim());
    }

    uploadPromise
      .then(function () {
        var servicesPromise = api
          .post('/api/users/streaming-services', { services: services })
          .catch(function () {});
        var profilePromise = api.put('/api/users/profile', {
          displayName: displayName,
          location: location,
          username: username,
          bio: bio,
          links: links,
          digestOptOut: digestOptOut,
        });
        return Promise.all([profilePromise, servicesPromise]).then(function (results) {
          return results[0];
        });
      })
      .then(function (result) {
        setSelectedFile(null);
        // Update auth context with new username and streaming services
        if (authTokens && result.data.username) {
          var updatedAuth = {
            token: authTokens.token,
            user: Object.assign({}, authTokens.user, {
              username: result.data.username,
              streamingServices: services.length > 0 ? services.join(',') : null,
            }),
          };
          setAuthTokens(updatedAuth);
        }
        setIsLoading(false);
        navigate('/profile/' + (result.data.username || authTokens.user.username));
      })
      .catch(function (e) {
        var message = e.response?.data?.reason || 'Failed to update profile.';
        setError(message);
        setIsLoading(false);
      });
  }

  var displayPicture = previewURL || (pictureURL ? API_BASE + pictureURL : '');

  return (
    <Card style={{ marginTop: '2rem' }}>
      <PageTitle>Edit Profile</PageTitle>
      {error && <FormError>{error}</FormError>}

      <div
        style={{ cursor: 'pointer' }}
        onClick={function () {
          fileInputRef.current?.click();
        }}
      >
        {displayPicture ? (
          <ProfilePicPreview src={displayPicture} alt="Profile" />
        ) : (
          <ProfilePicPlaceholder>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#999"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
            Add Photo
          </ProfilePicPlaceholder>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <Form>
        <Label>Your Name</Label>
        <Input
          type="text"
          value={displayName}
          onChange={function (e) {
            setDisplayName(e.target.value.slice(0, 50));
          }}
          placeholder="your name"
        />

        <Label>Location</Label>
        <Input
          type="text"
          value={location}
          onChange={function (e) {
            setLocation(e.target.value.slice(0, 100));
          }}
          placeholder="e.g. Olympia, WA"
        />

        <Label>Username</Label>
        <Input
          type="text"
          value={username}
          onChange={function (e) {
            setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_.]/g, ''));
          }}
          placeholder="username"
        />

        <Label>Bio</Label>
        <Textarea
          value={bio}
          onChange={function (e) {
            setBio(e.target.value);
          }}
          placeholder="Tell us about yourself..."
        />

        <Label>Links (comma-separated)</Label>
        <Input
          type="text"
          value={links}
          onChange={function (e) {
            setLinks(e.target.value);
          }}
          placeholder="https://twitter.com/you, https://yoursite.com"
        />

        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '0.8rem',
            marginBottom: '1rem',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={digestOptOut}
            onChange={function (e) {
              setDigestOptOut(e.target.checked);
            }}
          />
          Opt out of daily digest emails
        </label>
      </Form>

      {success && <SuccessMessage>{success}</SuccessMessage>}

      <div
        style={{
          width: '100%',
          marginTop: '1.5rem',
          paddingTop: '1.5rem',
          borderTop: '1px solid #eee',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
          <a
            href="/streaming"
            onClick={function (e) {
              e.preventDefault();
              navigate('/streaming');
            }}
            style={{
              fontSize: '1rem',
              fontWeight: 700,
              color: '#000',
              textDecoration: 'none',
            }}
          >
            Streaming Preferences &rsaquo;
          </a>
          <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.25rem' }}>
            Choose your streaming services and connect Apple Music
          </div>
        </div>

        <Button onClick={handleSave} disabled={isLoading} style={{ marginTop: '0.75rem' }}>
          {isLoading ? 'Saving...' : 'Save Profile'}
        </Button>
      </div>

      <DangerZone>
        {!showDelete ? (
          <DeleteButton
            onClick={function () {
              setShowDelete(true);
            }}
          >
            Delete my account
          </DeleteButton>
        ) : (
          <div>
            <p
              style={{
                fontSize: '0.85rem',
                color: '#333',
                marginBottom: '0.5rem',
                fontWeight: 600,
              }}
            >
              Are you sure you want to delete your account? This cannot be undone.
            </p>
            <p style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.75rem' }}>
              Your profile and personal data will be removed. Your posts and comments will remain
              but will show as "Deleted User." Type <strong>{username}</strong> to confirm.
            </p>
            <Input
              type="text"
              value={confirmText}
              onChange={function (e) {
                setConfirmText(e.target.value);
              }}
              placeholder={'Type ' + username + ' to confirm'}
              style={{ marginBottom: '0.75rem' }}
            />
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <ConfirmDeleteButton
                disabled={confirmText !== username || isDeleting}
                onClick={function () {
                  setIsDeleting(true);
                  api
                    .delete('/api/users/' + authTokens.user.id)
                    .then(function () {
                      localStorage.removeItem('hr_has_logged_in');
                      localStorage.removeItem('hr_onboarded');
                      setAuthTokens(null);
                      navigate('/', { replace: true });
                    })
                    .catch(function () {
                      setError('Failed to delete account. Please try again.');
                      setIsDeleting(false);
                    });
                }}
              >
                {isDeleting ? 'Deleting...' : 'Permanently Delete'}
              </ConfirmDeleteButton>
              <CancelButton
                onClick={function () {
                  setShowDelete(false);
                  setConfirmText('');
                }}
              >
                Cancel
              </CancelButton>
            </div>
          </div>
        )}
      </DangerZone>
    </Card>
  );
}

export default EditProfile;
